import React, { useState, useEffect, useRef } from "react";
import styles from "./AgentPlanList.module.css";
import AdminHeader from "../Header/AdminHeader";
import AdminFooter from "../Footer/AdminFooter";
import AdminLeftMenu from "../Navbar/AdminLeftMenu";
import Select from "react-select";
import DataTable from "react-data-table-component";
import { NODE_HOST_URL } from "../Dashboard/api/Api";
import { userloginDetails } from "../Utils/AdminAuth";
import Axios from "../../axios/Axios";

import {
  FiUsers,
  FiUserCheck,
  FiUserX,
  FiLayers,
  FiCheckCircle,
  FiXCircle,
  FiSearch,
  FiRotateCcw,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";

// ─── Table base styles (outside component — stable reference) ───────────────
const tableCustomStyles = {
  headRow: {
    style: {
      backgroundColor: "#f8fafc",
      borderBottom: "2px solid #e2e8f0",
      minHeight: "44px",
    },
  },
  headCells: {
    style: {
      fontSize: "11px",
      fontWeight: "700",
      color: "#334155",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      paddingLeft: "16px",
      paddingRight: "16px",
    },
  },
  rows: {
    style: {
      fontSize: "13px",
      color: "#0f172a",
      minHeight: "54px",
      borderBottom: "1px solid #f1f5f9",
    },
    highlightOnHoverStyle: {
      backgroundColor: "#f1f5f9",
      borderBottomColor: "#f1f5f9",
      outline: "none",
    },
  },
  cells: {
    style: {
      paddingLeft: "16px",
      paddingRight: "16px",
    },
  },
  pagination: {
    style: {
      borderTop: "1px solid #e2e8f0",
      fontSize: "12px",
      color: "#64748b",
      padding: "8px 16px",
      minHeight: "52px",
    },
  },
};

// ─── React-Select styles (outside component — stable reference) ──────────────
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "38px",
    fontSize: "13px",
    borderColor: state.isFocused ? "#4f46e5" : "#e2e8f0",
    borderWidth: "1.5px",
    borderRadius: "8px",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(79,70,229,0.09)" : "none",
    "&:hover": { borderColor: "#c7d2fe" },
  }),
  placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: "13px" }),
  singleValue: (base) => ({ ...base, fontSize: "13px" }),
  option: (base, state) => ({
    ...base,
    fontSize: "13px",
    backgroundColor: state.isSelected ? "#4f46e5" : state.isFocused ? "#f5f3ff" : "#fff",
  }),
  indicatorSeparator: () => ({ display: "none" }),
};

const DETAIL_PER_PAGE = 10;
const PAGE_SIZE = 10;

// ─── Skeleton components (module-level — never recreated) ────────────────────
const SkeletonStatCard = () => (
  <div className={styles.skeletonStatCard}>
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <div className={`${styles.skeleton} ${styles.skeletonIconBox}`} />
      <div style={{ flex: 1 }}>
        <div className={styles.skeleton} style={{ height: "20px", width: "48%", marginBottom: "8px" }} />
        <div className={styles.skeleton} style={{ height: "10px", width: "68%" }} />
      </div>
    </div>
  </div>
);

const SkeletonTableRows = () => (
  <div>
    <div className={styles.skeletonRowHeader}>
      {[80, 220, 100, 80, 80].map((w, i) => (
        <div key={i} className={styles.skeleton} style={{ width: w, height: "10px", flexShrink: 0 }} />
      ))}
    </div>
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className={styles.skeletonRow}>
        <div className={styles.skeleton} style={{ width: "44px", height: "14px", flexShrink: 0 }} />
        <div style={{ flex: 2, display: "flex", flexDirection: "column", gap: "7px" }}>
          <div className={styles.skeleton} style={{ height: "13px", width: "52%" }} />
          <div className={styles.skeleton} style={{ height: "10px", width: "34%" }} />
        </div>
        <div className={styles.skeleton} style={{ width: "44px", height: "14px", flexShrink: 0 }} />
        <div className={styles.skeleton} style={{ width: "44px", height: "14px", flexShrink: 0 }} />
        <div className={styles.skeleton} style={{ width: "44px", height: "14px", flexShrink: 0 }} />
      </div>
    ))}
  </div>
);

// ─── Expanded row (outside AgentPlanList — prevents remount on parent render) ─
const ExpandedRow = ({ data, userId, userToken }) => {
  // ── State ──────────────────────────────────────────────────────────────────
  const [agents, setAgents] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailPage, setDetailPage] = useState(1);
  const [detailTotalRows, setDetailTotalRows] = useState(0);

  // ── API ───────────────────────────────────────────────────────────────────
  // Mirrors the user_list pattern: sends page + per_page, reads pagination.total
  const fetchDetail = async (page) => {
    setLoadingDetail(true);
    try {
      const payload = {
        user_id: userId,
        token: userToken,
        method: "client_agent_list",
        client_id: data.id,
        page,
        per_page: DETAIL_PER_PAGE,
      };
      const res = await Axios.post(`${NODE_HOST_URL}/agent_management`, payload);
      if (res.data.success) {
        setAgents(res.data.data || []);
        setDetailTotalRows(res.data.pagination?.total || 0);
      } else {
        setAgents([]);
        setDetailTotalRows(0);
      }
    } catch {
      setAgents([]);
      setDetailTotalRows(0);
    } finally {
      setLoadingDetail(false);
    }
  };

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchDetail(detailPage);
  }, [detailPage, data.id]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handlePrev = () => setDetailPage((p) => Math.max(1, p - 1));
  const handleNext = () => setDetailPage((p) => p + 1);

  // ── Derived values ────────────────────────────────────────────────────────
  const totalPages = Math.ceil(detailTotalRows / DETAIL_PER_PAGE);
  const startItem = detailTotalRows === 0 ? 0 : (detailPage - 1) * DETAIL_PER_PAGE + 1;
  const endItem = Math.min(detailPage * DETAIL_PER_PAGE, detailTotalRows);
  const firstLetter = (data.username || "?").charAt(0).toUpperCase();

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={styles.expandedContainer}>
      <div className={styles.expandedInner}>

        {/* Left — user profile + stats */}
        <div className={styles.expandedLeft}>
          <div className={styles.expandedHeader}>
            <div className={styles.expandedAvatar}>{firstLetter}</div>
            <div>
              <div className={styles.expandedName}>{data.username}</div>
              <div className={styles.expandedMeta}>
                ID: {data.id}
              </div>
            </div>
          </div>

          <div className={styles.expandedStatRow}>
            <div className={styles.expandedStatBox}>
              <span className={styles.expandedStatVal}>{data.total_agents}</span>
              <div className={styles.expandedStatLabel}>Total</div>
            </div>
            <div className={styles.expandedStatBox}>
              <span className={`${styles.expandedStatVal} ${styles.statGreen}`}>
                {data.active_agents}
              </span>
              <div className={styles.expandedStatLabel}>Active</div>
            </div>
            <div className={styles.expandedStatBox}>
              <span className={`${styles.expandedStatVal} ${styles.statRed}`}>
                {data.inactive_agents}
              </span>
              <div className={styles.expandedStatLabel}>Inactive</div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className={styles.expandedDivider} />

        {/* Right — agent list with pagination */}
        <div className={styles.expandedRight}>
          <div className={styles.agentListTitle}>Agents</div>

          {loadingDetail ? (
            <table className={styles.agentMiniTable}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Mobile</th>
                  <th>Parent</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}>
                    <td><div className={styles.skeleton} style={{ height: "12px", width: "30px" }} /></td>
                    <td><div className={styles.skeleton} style={{ height: "12px", width: "110px" }} /></td>
                    <td><div className={styles.skeleton} style={{ height: "12px", width: "90px" }} /></td>
                    <td><div className={styles.skeleton} style={{ height: "18px", width: "54px", borderRadius: "20px" }} /></td>
                    <td><div className={styles.skeleton} style={{ height: "12px", width: "80px" }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : agents.length > 0 ? (
            <>
              <table className={styles.agentMiniTable}>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Mobile</th>
                    <th>Parent</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((agent, i) => {
                    const isActive = (agent.status || "").toLowerCase() === "active";
                    return (
                      <tr key={i}>
                        <td>{agent.id || agent.agent_id || "-"}</td>
                        <td>{agent.name || agent.agent_name || "-"}</td>
                        <td>{agent.mobile || agent.phone || "-"}</td>
                        {/* <td>
                          <span
                            className={`${styles.agentStatusBadge} ${isActive ? styles.agentStatusActive : styles.agentStatusInactive
                              }`}
                          >
                            {agent.status || "Unknown"}
                          </span>
                        </td> */}
                        <td>{`${agent.username}(${agent.user_id})`}</td>
                        <td>{agent.created ||  "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className={styles.detailPagination}>
                  <span className={styles.detailPagInfo}>
                    {startItem}–{endItem} of {detailTotalRows}
                  </span>
                  <div className={styles.detailPagButtons}>
                    <button
                      className={styles.detailPagBtn}
                      onClick={handlePrev}
                      disabled={detailPage === 1}
                    >
                      <FiChevronLeft />
                    </button>
                    <span className={styles.detailPagPage}>
                      {detailPage} / {totalPages}
                    </span>
                    <button
                      className={styles.detailPagBtn}
                      onClick={handleNext}
                      disabled={detailPage >= totalPages}
                    >
                      <FiChevronRight />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className={styles.expandedEmpty}>No agents found for this user.</div>
          )}
        </div>

      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const AgentPlanList = () => {

  // ── Auth ──────────────────────────────────────────────────────────────────
  const lStorage = userloginDetails();
  const userId = lStorage?.user?.userId;
  const userToken = lStorage?.token;

  // ── State ──────────────────────────────────────────────────────────────────
  const [filters, setFilters] = useState({ employee: null, reseller: null, client: null, search: "" });
  const debounceRef = useRef(null);
  const searchRef = useRef("");
  const [agentSummaryData, setAgentSummaryData] = useState({});
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);

  // ── API calls ──────────────────────────────────────────────────────────────
  const fetchSummary = async () => {
    setSummaryLoading(true);
    try {
      const payload = {
        user_id: userId,
        token: userToken,
        method: "summary",
      };
      const response = await Axios.post(`${NODE_HOST_URL}/agent_management`, payload);
      if (response.data.success) {
        setAgentSummaryData(response.data.data);
      }
    } catch {
      setAgentSummaryData({});
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchAgents = async (search, pageNum) => {
    setLoading(true);
    try {
      const payload = {
        user_id: userId,
        token: userToken,
        method: "user_list",
        filter: {
          emp_id: filters.employee?.value || 0,
          reseller_id: filters.reseller?.value || 0,
          client_id: filters.client?.value || 0,
        },
        page: pageNum,
        search: search,
      };
      const response = await Axios.post(`${NODE_HOST_URL}/agent_management`, payload);
      const result = response.data;
      if (result.success) {
        setAgents(result.data);
        setTotalRows(result.pagination.total);
      } else {
        setAgents([]);
      }
    } catch {
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      await fetchSummary();
      if (page === 1) await new Promise((r) => setTimeout(r, 500));
      fetchAgents(searchRef.current, page);
    };
    init();
  }, [page]);

  useEffect(() => {
    searchRef.current = filters.search;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (page !== 1) {
        setPage(1); // triggers [page] effect which reads searchRef.current
      } else {
        fetchAgents(filters.search, 1);
      }
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [filters.search]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    setFilters({ employee: null, reseller: null, client: null, search: "" });
  };

  const handleSearch = () => {
    setPage(1);
    fetchAgents();
  };

  const handleRefresh = () => {
    fetchAgents();
  };

  // ── Config / derived constants ────────────────────────────────────────────
  const stats = [
    {
      label: "Total Agents",
      value: agentSummaryData?.total_agents || 0,
      change: agentSummaryData?.percent_increase ? `+${agentSummaryData.percent_increase}%` : null,
      extra: agentSummaryData?.new_agents ? `${agentSummaryData.new_agents} new this month` : null,
      icon: <FiUsers />,
      bg: "#eef2ff",
      color: "#4f46e5",
    },
    {
      label: "Active Agents",
      value: agentSummaryData?.active_agents || 0,
      icon: <FiUserCheck />,
      bg: "#f0fdf4",
      color: "#16a34a",
    },
    {
      label: "Inactive Agents",
      value: agentSummaryData?.inactive_agents || 0,
      icon: <FiUserX />,
      bg: "#fdf4ff",
      color: "#a855f7",
    },
    {
      label: "Total Plans",
      value: agentSummaryData?.total_plans || 0,
      icon: <FiLayers />,
      bg: "#fffbeb",
      color: "#f59e0b",
    },
    {
      label: "Active Plans",
      value: agentSummaryData?.total_active_plans || 0,
      icon: <FiCheckCircle />,
      bg: "#f0fdf4",
      color: "#16a34a",
    },
    {
      label: "Inactive Plans",
      value: agentSummaryData?.total_inactive_plans || 0,
      icon: <FiXCircle />,
      bg: "#fef2f2",
      color: "#ef4444",
    },
  ];

  const tableColumns = [
    {
      name: "ID",
      selector: (row) => row.id,
      cell: (row) => <span className={styles.idCell}>{row.id}</span>,
      width: "80px",
      sortable: true,
    },
    {
      name: "User Name",
      cell: (row) => (
        <div className={styles.userCell}>
          <div className={styles.userName}>{row.username}</div>
        </div>
      ),
      grow: 2,
      sortable: true,
      selector: (row) => row.username,
    },
    {
      name: "Total Agents",
      selector: (row) => row.total_agents,
      cell: (row) => <span className={styles.countTotal}>{row.total_agents}</span>,
      sortable: true,
      center: true,
    },
    {
      name: "Active",
      selector: (row) => row.active_agents,
      cell: (row) => <span className={styles.countActive}>{row.active_agents}</span>,
      sortable: true,
      center: true,
    },
    {
      name: "Inactive",
      selector: (row) => row.inactive_agents,
      cell: (row) => <span className={styles.countInactive}>{row.inactive_agents}</span>,
      sortable: true,
      center: true,
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="wrapper">
      <AdminLeftMenu />

      <div className="main">
        <AdminHeader />

        <div className="container-fluid p-3 p-md-4">

          {/* PAGE HEADER */}
          <div className="d-flex justify-content-between align-items-center mt-3 mb-4">
            <div>
              <h5 className={styles.pageTitle}>Agent Management</h5>
              <div className={styles.subtitle}>
                Monitor and manage agents, billing, and performance across all clients.
              </div>
            </div>
          </div>

          {/* STATS ROW */}
          <div className="row mb-4">
            {summaryLoading
              ? Array.from({ length: 6 }).map((_, i) => (
                <div className="col-md-2 mb-3" key={i}>
                  <SkeletonStatCard />
                </div>
              ))
              : stats.map((item, i) => (
                <div className="col-md-2 mb-3" key={i}>
                  <div className={styles.statCard} style={{ "--accent": item.color }}>
                    <div className="d-flex align-items-center" style={{ gap: "12px" }}>
                      <div
                        className={styles.compactIcon}
                        style={{ background: item.bg, color: item.color }}
                      >
                        {item.icon}
                      </div>
                      <div>
                        <div className={styles.compactValue}>{item.value}</div>
                        <div className={styles.compactLabel}>{item.label}</div>
                      </div>
                    </div>
                    {(item.change || item.extra) && (
                      <div className="d-flex align-items-center mt-2" style={{ gap: "6px" }}>
                        {item.change && <span className={styles.compactChange}>{item.change}</span>}
                        {item.extra && <span className={styles.compactSub}>{item.extra}</span>}
                      </div>
                    )}
                  </div>
                </div>
              ))
            }
          </div>

          {/* FILTER SECTION */}
          {/* <div className={styles.sectionHeader} style={{ gap: "8px" }}>
            <FiUsers style={{ color: "#4f46e5", fontSize: "15px" }} />
            <span>Filter Agents</span>
          </div> */}

          {/* <div className={`${styles.filterCard} mb-4`}>
            <div className={styles.filterRow}>
              <div className={styles.filterField}>
                <label className={styles.formLabel}>Employee</label>
                <Select
                  isSearchable
                  placeholder="Select..."
                  value={filters.employee}
                  onChange={(v) => handleFilterChange("employee", v)}
                  styles={selectStyles}
                />
              </div>

              <div className={styles.filterField}>
                <label className={styles.formLabel}>Reseller</label>
                <Select
                  isSearchable
                  placeholder="Select..."
                  value={filters.reseller}
                  onChange={(v) => handleFilterChange("reseller", v)}
                  styles={selectStyles}
                />
              </div>

              <div className={styles.filterField}>
                <label className={styles.formLabel}>Client</label>
                <Select
                  isSearchable
                  placeholder="Select..."
                  value={filters.client}
                  onChange={(v) => handleFilterChange("client", v)}
                  styles={selectStyles}
                />
              </div>

              <div className={styles.filterFieldWide}>
                <label className={styles.formLabel}>Search</label>
                <div className={styles.searchBox}>
                  <FiSearch />
                  <input
                    placeholder="Name or ID"
                    value={filters.search}
                    onChange={(e) => handleFilterChange("search", e.target.value)}
                  />
                </div>
              </div>

              <div className={styles.filterActions}>
                <button className={styles.btnReset} onClick={handleReset}>
                  <FiRotateCcw /> Reset
                </button>
                <button className={styles.btnSearch} onClick={handleSearch}>
                  <FiSearch /> Search
                </button>
              </div>
            </div>
          </div> */}

          {/* TABLE */}
          <div className={styles.tableWrapper}>
            <div className={styles.tableHeaderRow}>
              <div>
                <div className={styles.tableTitle}>Agent Users</div>
                <div className={styles.tableMeta}>
                  Total {totalRows} · Page {page} of {Math.max(1, Math.ceil(totalRows / PAGE_SIZE))}
                </div>
              </div>
              <div className={styles.tableActions}>
                <div className={styles.searchBox}>
                  <FiSearch />
                  <input
                    placeholder="Search by username"
                    value={filters.search}
                    onChange={(e) => handleFilterChange("search", e.target.value)}
                  />
                  {filters.search && (
                    <button
                      onClick={() => handleFilterChange("search", "")}
                      style={{ background: "none", border: "none", cursor: "pointer", padding: "0 4px", color: "#94a3b8", display: "flex", alignItems: "center" }}
                    >
                      &#x2715;
                    </button>
                  )}
                </div>
              </div>
            </div>
            {loading ? (
              <SkeletonTableRows />
            ) : agents.length > 0 ? (
              <DataTable
                columns={tableColumns}
                data={agents}
                customStyles={tableCustomStyles}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                onChangePage={(newPage) => setPage(newPage)}
                highlightOnHover
                pointerOnHover
                expandableRows
                expandOnRowClicked
                expandableRowsHideExpander
                expandableRowsComponent={ExpandedRow}
                expandableRowsComponentProps={{ userId, userToken }}
              />
            ) : (
              <div className={styles.emptyState}>No records found</div>
            )}
          </div>

        </div>

        <AdminFooter />
      </div>
    </div>
  );
};

export default AgentPlanList;
